import React, { useEffect, useState } from "react";
import { Cookie, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const STORAGE_KEY = "td_cookie_consent";  // "essential"

const COPY = {
  en: {
    title: "Privacy & essential cookies",
    body: "TradeDocs currently uses only essential storage for sign-in security, cookie choice and language preferences. We do not run advertising, analytics or session-recording cookies.",
    accept: "Understood",
    manage: "Cookie Policy",
    privacy: "Privacy Policy",
  },
  it: {
    title: "Privacy e cookie essenziali",
    body: "TradeDocs utilizza attualmente solo memoria essenziale per la sicurezza dell'accesso, la scelta sui cookie e la lingua. Non utilizziamo cookie pubblicitari, analytics o registrazioni di sessione.",
    accept: "Ho capito",
    manage: "Cookie Policy",
    privacy: "Informativa privacy",
  },
  de: {
    title: "Datenschutz & notwendige Cookies",
    body: "TradeDocs nutzt derzeit nur notwendige Speicherung für Anmeldesicherheit, Cookie-Auswahl und Spracheinstellungen. Werbe-, Analyse- oder Sitzungsaufzeichnungs-Cookies werden nicht eingesetzt.",
    accept: "Verstanden",
    manage: "Cookie-Richtlinie",
    privacy: "Datenschutzerklärung",
  },
  es: {
    title: "Privacidad y cookies esenciales",
    body: "TradeDocs utiliza únicamente almacenamiento esencial para proteger el inicio de sesión, recordar su elección de cookies y guardar el idioma. No utilizamos cookies publicitarias, analíticas ni grabación de sesiones.",
    accept: "Entendido",
    manage: "Política de cookies",
    privacy: "Política de privacidad",
  },
  zh: {
    title: "隐私与必要 Cookie",
    body: "TradeDocs 仅使用登录安全、Cookie 选择和语言偏好所必需的存储。我们不使用广告、分析或会话录制 Cookie。",
    accept: "已了解",
    manage: "Cookie 政策",
    privacy: "隐私政策",
  },
  ru: {
    title: "Конфиденциальность и обязательные cookie",
    body: "TradeDocs использует только обязательное хранилище для безопасности входа, сохранения выбора cookie и языка. Мы не используем рекламные и аналитические cookie и не записываем сессии.",
    accept: "Понятно",
    manage: "Политика cookie",
    privacy: "Политика конфиденциальности",
  },
};

export default function CookieBanner() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [decided, setDecided] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setDecided(!!stored);
  }, []);

  const decide = (value) => {
    localStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem("td_cookie_consent_at", new Date().toISOString());
    setDecided(true);
  };

  if (decided) return null;

  return (
    <div
      role="dialog"
      aria-label="cookie consent"
      data-testid="cookie-banner"
      className="fixed bottom-[max(.75rem,env(safe-area-inset-bottom))] left-3 right-3 sm:left-4 sm:right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60] card-surface border-[var(--td-gold)] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] p-4 backdrop-blur-xl max-h-[calc(100dvh-1.5rem)] overflow-y-auto"
      style={{ background: "rgba(18, 22, 32, 0.96)" }}
    >
      <div className="flex items-start gap-3 mb-3">
        <Cookie size={18} className="text-[var(--td-gold)] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-serif text-lg text-[var(--td-text)] leading-tight mb-1.5">{c.title}</div>
          <p className="text-xs text-[var(--td-text-2)] leading-relaxed">{c.body}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 mt-4">
        <button
          onClick={() => decide("essential")}
          data-testid="cookie-accept"
          className="btn-gold !py-2 !px-3 text-[10px] font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 w-full"
        >
          <Check size={11} /> {c.accept}
        </button>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 py-1">
          <a href="/legal/cookies" className="text-[10px] font-mono uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-gold)] transition-colors underline underline-offset-4">{c.manage}</a>
          <a href="/privacy-policy" className="text-[10px] font-mono uppercase tracking-widest text-[var(--td-text-3)] hover:text-[var(--td-gold)] transition-colors underline underline-offset-4">{c.privacy}</a>
        </div>
      </div>
    </div>
  );
}
